/**
 * El mensaje que se le muestra a una persona cuando falla un pedido al backend.
 *
 * - El texto sale de `err.response.data.error` (o `.message`, que usan las rutas
 *   de Precios Dinámicos). Nunca `err.message`: es el de axios, en inglés
 *   («Request failed with status code 422»).
 * - Un 422 de `validateBody`/`validateQuery` trae el camino del campo
 *   («filtro.estado: …»): nombres internos. Ése se reemplaza por `porDefecto`.
 *   Un issue de Zod en la RAÍZ del cuerpo (sin campo delante) llega igual de
 *   crudo, pero sin nombre: «: Expected object, received null» — también es
 *   `porDefecto` (el «: » sin nada antes ya avisa que no es para una persona).
 * - El 400 genérico «Datos inválidos» del alta de reglas trae el detalle en
 *   `details` (con nombres de campo): también `porDefecto`.
 * - Un 5xx o un error sin respuesta (red caída): `porDefecto`.
 * - Un 413 sin texto (sin cuerpo JSON): lo cortó el proxy (nginx,
 *   `client_max_body_size`) antes de llegar al backend → `PESA_DE_MAS`.
 */
const PESA_DE_MAS = 'El archivo pesa más de lo que acepta el servidor.';
const CAMINO_DE_CAMPO = /^[A-Za-z_][\w.[\]]*: /;
const ISSUE_EN_LA_RAIZ = /^: /;

export function mensajeDeError(err: unknown, porDefecto: string): string {
  const r = (
    err as { response?: { status?: number; data?: { error?: unknown; message?: unknown } } }
  )?.response;
  if (!r || typeof r.status !== 'number' || r.status >= 500) return porDefecto;
  const texto = typeof r.data?.error === 'string' ? r.data.error : r.data?.message;
  if (typeof texto !== 'string' || texto.trim() === '')
    return r.status === 413 ? PESA_DE_MAS : porDefecto;
  if (r.status === 422 && (CAMINO_DE_CAMPO.test(texto) || ISSUE_EN_LA_RAIZ.test(texto)))
    return porDefecto;
  if (r.status === 400 && texto === 'Datos inválidos') return porDefecto;
  return texto;
}

/**
 * Los códigos con que el backend distingue un 409 de otro (campo `codigo` del
 * cuerpo). El texto de `mensajeDeError` es para mostrar; el código es para que la
 * pantalla decida QUÉ hacer (recargar, ofrecer reintento, listar lo que falta).
 */
export type CodigoDeError =
  | 'ESTADO_CAMBIO'
  | 'COTIZACION_CAMBIO'
  | 'FALTAN_DATOS'
  | 'APROBACION_PENDIENTE'
  | 'ESPEJO_DESINCRONIZADO'
  | 'CALCULO_DESACTUALIZADO'
  // Reglas de la demo (spec 343, anexo §3 y §5): llegan con un 403. El freno lo arma el mapa de textos.
  | 'TOPE_DE_PRODUCTOS_DEMO'
  | 'SOLO_EJEMPLOS_EN_LA_DEMO'
  // Tienda de la prueba (feat/decisiones-subida-2): soporte no cambia la prueba; sin tienda; archivada.
  | 'SOPORTE_SOLO_MIRA'
  | 'DEMO_SIN_TIENDA'
  | 'EMPRESA_ARCHIVADA';

const CODIGOS: readonly string[] = [
  'ESTADO_CAMBIO',
  'COTIZACION_CAMBIO',
  'FALTAN_DATOS',
  'APROBACION_PENDIENTE',
  'ESPEJO_DESINCRONIZADO',
  'CALCULO_DESACTUALIZADO',
  'TOPE_DE_PRODUCTOS_DEMO',
  'SOLO_EJEMPLOS_EN_LA_DEMO',
  'SOPORTE_SOLO_MIRA',
  'DEMO_SIN_TIENDA',
  'EMPRESA_ARCHIVADA',
] satisfies CodigoDeError[];

/** Lo que falta para poder enviar a aprobación (`FALTAN_DATOS`). */
export interface FaltanteParaEnviar {
  clave: 'cliente' | 'oportunidad' | 'costo' | 'item';
  texto: string;
  /** Sólo en `clave: 'item'`: el ítem al que lleva el enlace. */
  itemId?: number;
}

function cuerpoDe(err: unknown): { status: number; data: Record<string, unknown> } | null {
  const r = (err as { response?: { status?: unknown; data?: unknown } })?.response;
  if (!r || typeof r.status !== 'number' || r.status >= 500) return null;
  if (!r.data || typeof r.data !== 'object') return null;
  return { status: r.status, data: r.data as Record<string, unknown> };
}

/** El `codigo` conocido de un error del backend, o `null` (sin código, desconocido, 5xx o sin respuesta). */
export function codigoDeError(err: unknown): CodigoDeError | null {
  const codigo = cuerpoDe(err)?.data.codigo;
  return typeof codigo === 'string' && CODIGOS.includes(codigo) ? (codigo as CodigoDeError) : null;
}

/** La lista `faltantes` de un `FALTAN_DATOS`; `[]` en cualquier otro caso. */
export function faltantesDeError(err: unknown): FaltanteParaEnviar[] {
  if (codigoDeError(err) !== 'FALTAN_DATOS') return [];
  const faltantes = cuerpoDe(err)?.data.faltantes;
  return Array.isArray(faltantes) ? (faltantes as FaltanteParaEnviar[]) : [];
}

/**
 * El estado del cálculo pendiente que trae un `CALCULO_DESACTUALIZADO` (el servidor lo entrega con el mismo
 * formato que al leer la cotización); `null` en cualquier otro caso. La pantalla lo usa para repintar la banda.
 */
export function pendienteDeError(err: unknown): {
  motivos: Array<'FLETE' | 'REGLAS' | 'REDONDEO'>;
  totalGuardado: number;
  totalCorregido: number;
  faltaFlete: number[];
} | null {
  if (codigoDeError(err) !== 'CALCULO_DESACTUALIZADO') return null;
  const p = cuerpoDe(err)?.data.pendiente as Record<string, unknown> | undefined;
  if (!p || !Array.isArray(p.motivos)) return null;
  return {
    motivos: p.motivos as Array<'FLETE' | 'REGLAS' | 'REDONDEO'>,
    totalGuardado: Number(p.totalGuardado),
    totalCorregido: Number(p.totalCorregido),
    faltaFlete: Array.isArray(p.faltaFlete) ? (p.faltaFlete as number[]) : [],
  };
}
